import { useState, useRef, useEffect, useContext, useMemo } from "react";
import { Modal } from "@material-ui/core";
import { useStyles } from "../../Styles/styles";
import { UserContext } from "../../Contexts/UserContext";
import { useMutation } from "@apollo/client";
import { SetupContext } from "../../Contexts/SetupContext";
import clsx from "clsx";
import { SEND_QUERY_REQUEST } from "../../Mutations/SendQueryRequest";
import { SET_STATUS } from "../../Mutations/SetStatus";
import TopHalf from "./TopHalf";
import BottomHalf from "./BottomHalf";
import ButtonRow from "./ButtonRow";
import {
  handleChange,
  handleClose,
  handleRunQuery,
  variableFields,
  initializeFormState,
  initializeQueryState,
  handleMutationCompleted,
  getDefaultQuery,
  isQueryFormValid,
} from "./dmvQueryModalHandlers";
import PropTypes from "prop-types";
import { allQueryTypes } from "./queryTypes";

const DmvQueryModal = ({
  modalOpen,
  setModalOpen,
  incidentId,
  defaultQuery,
  setTabIndex = null,
  setPopper,
}) => {
  const classes = useStyles();
  const { user, nightMode, tokenDisabled } = useContext(UserContext);
  const { setup, vehicleQueryCode, personQueryCode } = useContext(SetupContext);
  const [setStatus] = useMutation(SET_STATUS);

  const [querySelection, setQuerySelection] = useState(
    defaultQuery || getDefaultQuery()
  );
  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);
  const [queryState, setQueryState] = useState({});
  const { setupLists } = setup || {};
  const { states, licenseTypes } = setupLists || [];
  const [formState, setFormState] = useState({});
  const [expandUserInfo, setExpandUserInfo] = useState(false);

  const disallowedLicenseTypes = useMemo(() => ["SN - SNOWMOBILE"], []);

  const filteredLicenseTypes = useMemo(() => {
    return (licenseTypes || [])?.filter(
      type => !disallowedLicenseTypes.includes(type.name)
    );
  }, [licenseTypes, disallowedLicenseTypes]);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, []);

  useEffect(() => {
    initializeQueryState(querySelection, filteredLicenseTypes, setQueryState);
  }, [filteredLicenseTypes, querySelection]);

  useEffect(() => {
    if (defaultQuery) {
      setQuerySelection(defaultQuery);
    }
  }, [defaultQuery]);

  useEffect(() => {
    if (user?.officer?.deptAttribute) {
      initializeFormState(user, states, setFormState, setExpandUserInfo);
    }
  }, [states, user]);

  const [sendQueryRequest] = useMutation(SEND_QUERY_REQUEST, {
    onCompleted: data =>
      handleMutationCompleted(data, setPopper, setModalOpen, setTabIndex),
    onError: err => setPopper({ open: true, content: err?.message }),
  });

  const submitArgs = {
    formState,
    queryState,
    querySelection,
    user,
    incidentId,
    sendQueryRequest,
    setStatus,
    handleClose,
    vehicleQueryCode,
    personQueryCode,
  };

  const closeArgs = {
    setModalOpen,
    setFormState,
    user,
    states,
  };

  return (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      className={classes.modal}
      data-testid="dmv-query-modal"
      keepMounted={false}
    >
      <form className={classes.dmvModals}>
        <div className={classes.modalContent}>
          <TopHalf
            expandUserInfo={expandUserInfo}
            setExpandUserInfo={setExpandUserInfo}
            nightMode={nightMode}
            formState={formState}
            handleChange={handleChange}
            setFormState={setFormState}
            querySelection={querySelection}
            setQuerySelection={setQuerySelection}
            user={user}
            queryTypes={allQueryTypes}
          />
          <div style={{ margin: "10px" }}>
            <BottomHalf
              queryState={queryState}
              setQueryState={setQueryState}
              queryType={querySelection?.name}
              licenseTypes={filteredLicenseTypes}
              user={user}
              destination={formState?.destination}
            >
              {variableFields(
                querySelection,
                formState,
                setFormState,
                handleChange,
                states,
                classes,
                inputLabel,
                labelWidth,
                user
              )}
            </BottomHalf>
          </div>
        </div>
        <ButtonRow
          handleSubmit={e =>
            handleRunQuery(
              e,
              submitArgs,
              closeArgs,
              isQueryFormValid,
              tokenDisabled
            )
          }
          handleClose={() => handleClose(closeArgs)}
          buttonStyle={clsx(classes.buttonHover, classes.navBarButtons)}
          disabled={!querySelection || tokenDisabled}
        />
        {tokenDisabled && (
          <p>
            Switch access is currently blocked due to the SoftToken requirement
            being disabled.
          </p>
        )}
      </form>
    </Modal>
  );
};

DmvQueryModal.propTypes = {
  modalOpen: PropTypes.bool.isRequired,
  setModalOpen: PropTypes.func.isRequired,
  incidentId: PropTypes.string,
  defaultQuery: PropTypes.object,
  setTabIndex: PropTypes.func,
  setPopper: PropTypes.func.isRequired,
};

export default DmvQueryModal;
